import {
  deleteSSRCookie,
  getSSRCookie,
  setSSRCookie,
  TCookieOptions,
} from '@/common/cookies';
import {
  TAuthenticatorChallengeCookie,
  TPasscodeAndAuthenticatorCookie,
  TPasscodeAndAuthenticatorValue,
} from '@/modules/auth/types';
import { toBoolean } from '@/utils/string';
import { getAuthUID, setAuthToken } from '@/modules/auth/utils/authToken';

const PASSCODE_AND_AUTHENTICATOR_COOKIE_NAME = 'passcodeAndAuthenticator';
const AUTHENTICATOR_CHALLENGE_COOKIE_NAME = 'authenticatorChallenge';

export const isMultiAccountPasscodeSupported = () => {
  return toBoolean(process.env.PASSCODE_SUPPORT_MULTI_ACCOUNT);
};

export const getPasscodeSupportedPlatforms = () => {
  const isEnabled: boolean = toBoolean(
    process.env.NEXT_PUBLIC_PASSCODE_ENABLED!
  );
  const isDesktopSupported = toBoolean(
    process.env.NEXT_PUBLIC_PASSCODE_SUPPORT_DESKTOP!
  );
  return {
    mobileAndTablet: isEnabled,
    desktop: isEnabled && isDesktopSupported,
  };
};

export const getPasscodesAndAuthenticators = (
  req: TServerSideRequest
): TPasscodeAndAuthenticatorCookie | null => {
  return getSSRCookie(req, PASSCODE_AND_AUTHENTICATOR_COOKIE_NAME);
};

export const getPasscodeAndAuthenticatorByUID = (
  req: TServerSideRequest,
  uid: string
): TPasscodeAndAuthenticatorValue | undefined => {
  return uid ? getPasscodesAndAuthenticators(req)?.[uid] : undefined;
};

export const getCurrentPasscodeAndAuthenticator = (
  req: TServerSideRequest
): TPasscodeAndAuthenticatorValue | undefined => {
  return getPasscodeAndAuthenticatorByUID(req, getAuthUID(req)!);
};

export const deletePasscodesAndAuthenticators = (res: TServerSideResponse) => {
  deleteSSRCookie(res, PASSCODE_AND_AUTHENTICATOR_COOKIE_NAME);
};

export const setCurrentPasscodeOrAuthenticator = (
  context: TServerSideContext,
  newValue: Partial<TPasscodeAndAuthenticatorValue>,
  options: TCookieOptions = {}
) => {
  const { req, res } = context;
  const uid = getAuthUID(req);
  if (!uid) {
    throw new Error('کاربر یافت نشد!');
  }

  const currentValues: TPasscodeAndAuthenticatorCookie =
    getPasscodesAndAuthenticators(req) || {};

  const newValues: TPasscodeAndAuthenticatorCookie = {
    ...(isMultiAccountPasscodeSupported() ? currentValues : {}),
    [uid]: { ...(currentValues?.[uid] ?? {}), ...newValue },
  };

  const passcode = newValues?.[uid]?.['pCode'];
  if (!passcode) delete newValues[uid];

  setAuthToken(context, { isNeedPasscode: !!passcode });

  if (Object.keys(newValues).length) {
    const maxAge: number = new Date().getTime() + 10 * 365 * 24 * 60 * 60;
    setSSRCookie(res, PASSCODE_AND_AUTHENTICATOR_COOKIE_NAME, newValues, {
      maxAge,
      ...options,
    });
  } else {
    deletePasscodesAndAuthenticators(res);
  }
};

//---------------------------------------

export const setCurrentPasscode = (
  context: TServerSideContext,
  newPasscode: string,
  options: TCookieOptions = {}
) => {
  return setCurrentPasscodeOrAuthenticator(
    context,
    { pCode: newPasscode },
    options
  );
};

export const setCurrentAuthenticator = (
  context: TServerSideContext,
  newAuthenticatorId: string,
  options: TCookieOptions = {}
) => {
  return setCurrentPasscodeOrAuthenticator(
    context,
    { aId: newAuthenticatorId },
    options
  );
};

export const getCurrentPasscode = (
  req: TServerSideRequest
): string | undefined => {
  return getCurrentPasscodeAndAuthenticator(req)?.['pCode'];
};

export const getCurrentAuthenticator = (
  req: TServerSideRequest
): string | undefined => {
  return getCurrentPasscodeAndAuthenticator(req)?.['aId'];
};

//---------------------------------

export const setAuthenticatorChallenge = (
  res: TServerSideResponse,
  value: TAuthenticatorChallengeCookie,
  options?: TCookieOptions
) => {
  const maxAge: number = new Date().getTime() + 1.5 * 60;
  setSSRCookie(res, AUTHENTICATOR_CHALLENGE_COOKIE_NAME, value, {
    maxAge,
    ...options,
  });
};

export const deleteAuthenticatorChallenge = (res: TServerSideResponse) => {
  deleteSSRCookie(res, AUTHENTICATOR_CHALLENGE_COOKIE_NAME);
};

export const getAuthenticatorChallenge = ({ req, res }: TServerSideContext) => {
  const challenge = getSSRCookie(req, AUTHENTICATOR_CHALLENGE_COOKIE_NAME);
  deleteAuthenticatorChallenge(res);
  return challenge;
};
